'use client'

import { lazy, Suspense } from 'react'
import type { PhoneInputProps } from './PhoneInput'

/**
 * `PhoneInput`, deferred.
 *
 * `PhoneInput` pulls libphonenumber-js — **152 KB raw / ~39 KB brotli / 53 ms of
 * CPU**, measured as one of the largest single requests the storefront makes.
 * The country list, the as-you-type formatter and the length check all need the
 * metadata, so there is no smaller build of it to reach for; the only lever is
 * *when* it loads.
 *
 * **Every reference has to go through here, or none of them count.** webpack
 * puts libphonenumber in one shared vendor chunk, and a single static import
 * anywhere makes that chunk initial for *every* route that touches it — 21 of
 * them, including the homepage. Deferring it in `FormRender` alone was measured
 * at exactly zero bytes while `/bookings/[slug]` still imported it statically.
 * That is why the raw `./PhoneInput` module is off-limits to storefront code and
 * guarded by `apps/storefront/__tests__/phoneInputLazyBoundary.test.ts`.
 *
 * The portal imports `PhoneInput` directly and should keep doing so: it is a
 * Vite SPA with one bundle and no route-level initial chunks, so there is
 * nothing to win there and a Suspense boundary would only add a flicker.
 *
 * The fetch starts when this element first renders — not on focus — so the gap
 * is one chunk request beginning at form render, not at the moment the user
 * reaches the field.
 */
const PhoneInput = lazy(() => import('./PhoneInput'))

export default function LazyPhoneInput(props: PhoneInputProps) {
  return (
    <Suspense fallback={<PhoneInputPlaceholder {...props} />}>
      <PhoneInput {...props} />
    </Suspense>
  )
}

/**
 * Occupies exactly the box `PhoneInput` will occupy — same flex row, same
 * paddings, same borders, same radii. Reserving the space is the whole point:
 * a collapsed fallback would reintroduce the layout shift that metric-matched
 * font fallbacks were just built to remove.
 *
 * `readOnly` rather than `disabled` so the field keeps its place in the tab
 * order and stays focusable; a user who tabs here mid-fetch lands where they
 * expect instead of being skipped past. `aria-busy` tells assistive tech the
 * control is still arriving.
 */
function PhoneInputPlaceholder({
  className,
  placeholder = 'Phone number',
  id,
  required,
  disabled,
  describedById,
}: PhoneInputProps) {
  return (
    <div className={`flex items-stretch ${className ?? ''}`} aria-busy="true">
      <span
        aria-hidden="true"
        className="flex items-center justify-end gap-1.5 px-2.5 py-2 min-w-[4.625rem] rounded-l-lg border border-gray-200 bg-gray-50 text-sm whitespace-nowrap text-gray-400"
      >
        {/* Same chevron the real trigger draws, so the shape doesn't change on
            swap. `min-w` reserves the flag + dial-code width the real trigger
            measures at (74px for a 1–2 digit dial code); without it the input's
            left edge jumps ~41px when the chunk lands. */}
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </span>
      <input
        id={id}
        type="tel"
        autoComplete="tel"
        inputMode="tel"
        readOnly
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        aria-describedby={describedById}
        className="flex-1 px-3 py-2 rounded-r-lg border border-gray-200 border-l-0 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)] disabled:bg-gray-50 disabled:text-gray-400"
      />
    </div>
  )
}
